import { useEffect, useState } from "react";
import { useNavV5 } from "@/features/accountNavV5/lib/useNavV5";
import { isAppWindow } from "@/shared/lib/appWindow";
import {
    APP_SHELL_ADMIN_HINT_KEY,
    APP_SHELL_ATTR,
    APP_SHELL_FOR_ALL,
    APP_SHELL_OPT_OUT_KEY,
    APP_SHELL_PREVIEW,
    APP_SHELL_QUERY,
    APP_WINDOW_ATTR,
    TAB_SECTIONS_ON,
} from "../model/config";

/**
 * Включена ли оболочка «как в приложении» для этого человека и этого экрана.
 *
 *   phone     = живой matchMedia(APP_SHELL_QUERY) — тот же запрос, что у
 *               «Аналитики и баллов»; следует за поворотом телефона;
 *   allowed   = APP_SHELL_FOR_ALL или меню V5 (админ, не «смотреть как
 *               пользователь»; пока грузится список админов — нет, кроме
 *               того, кого этот браузер помнит админом: APP_SHELL_ADMIN_HINT_KEY);
 *   enabled   = APP_SHELL_PREVIEW и allowed и phone и не выключено плиткой
 *               «Прежний вид сайта (телефон)» (APP_SHELL_OPT_OUT_KEY);
 *   appWindow = страница открыта в окне приложения (?source=app или
 *               referrer android-app://…, запоминается на вкладку).
 *
 * Пока enabled ложно, страница рисует ровно сегодняшний сайт.
 */
export interface AppShellState {
    enabled: boolean;
    /** Список админов ещё грузится (ответ пока «нет»). */
    loading: boolean;
    appWindow: boolean;
    phone: boolean;
}

const matches = (query: string): boolean =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;

/** Живой matchMedia: меняется на повороте и при смене ширины окна. */
export const useMatchMedia = (query: string): boolean => {
    const [on, setOn] = useState(() => matches(query));
    useEffect(() => {
        if (typeof window.matchMedia !== "function") return;
        const mq = window.matchMedia(query);
        const update = () => setOn(mq.matches);
        update();
        if (typeof mq.addEventListener === "function") {
            mq.addEventListener("change", update);
            return () => mq.removeEventListener("change", update);
        }
        // Safari до 14
        mq.addListener(update);
        return () => mq.removeListener(update);
    }, [query]);
    return on;
};

// ── «Прежний вид сайта (телефон)» ─────────────────────────────────────────

const optOutListeners = new Set<(off: boolean) => void>();

export const getAppShellOptOut = (): boolean => {
    try {
        return localStorage.getItem(APP_SHELL_OPT_OUT_KEY) === "1";
    } catch {
        return false;
    }
};

/** Плитка админа: true — у этого браузера оболочки нет (до обратного нажатия). */
export const setAppShellOptOut = (off: boolean): void => {
    try {
        if (off) localStorage.setItem(APP_SHELL_OPT_OUT_KEY, "1");
        else localStorage.removeItem(APP_SHELL_OPT_OUT_KEY);
    } catch {
        /* приватный режим — переключатель просто не запомнится */
    }
    optOutListeners.forEach((fn) => fn(off));
};

/** Живой признак «Прежний вид сайта (телефон)»: меняется и плиткой, и в другой вкладке. */
export const useAppShellOptOut = (): boolean => {
    const [off, setOff] = useState(getAppShellOptOut);
    useEffect(() => {
        const fn = (v: boolean) => setOff(v);
        optOutListeners.add(fn);
        const onStorage = (e: StorageEvent) => {
            if (e.key === APP_SHELL_OPT_OUT_KEY) setOff(getAppShellOptOut());
        };
        window.addEventListener("storage", onStorage);
        return () => {
            optOutListeners.delete(fn);
            window.removeEventListener("storage", onStorage);
        };
    }, []);
    return off;
};

// ── Админ на этом браузере — для первого рисунка ──────────────────────────
//
// Список динамических админов приходит с сервера не сразу; до него useNavV5
// отвечает «нет», и кабинет такого админа сначала рисовался прежним: с фото
// фона (оно успевало скачаться), прежней шапкой и главной кабинета. Поэтому
// подтверждённый админ запоминается (его адрес), и пока список грузится, тот
// же человек сразу получает оболочку. Список сказал «нет» — память стирается.

/** Адрес, который этот браузер помнит админом (строчными); нет — "". */
export const getShellAdminHint = (): string => {
    try {
        return (localStorage.getItem(APP_SHELL_ADMIN_HINT_KEY) || "").toLowerCase();
    } catch {
        return "";
    }
};

const rememberShellAdmin = (email: string, admin: boolean): void => {
    const id = email.toLowerCase();
    try {
        if (admin) {
            if (localStorage.getItem(APP_SHELL_ADMIN_HINT_KEY) !== id) localStorage.setItem(APP_SHELL_ADMIN_HINT_KEY, id);
        } else if (getShellAdminHint() === id) {
            localStorage.removeItem(APP_SHELL_ADMIN_HINT_KEY);
        }
    } catch {
        /* приватный режим — просто не запомнится */
    }
};

export const useAppShell = (): AppShellState => {
    const phone = useMatchMedia(APP_SHELL_QUERY);
    const { enabled: navV5, isRealAdmin, loading: adminLoading, email, previewAsUser } = useNavV5();
    const optOut = useAppShellOptOut();
    // Признак окна приложения запоминается при первом взгляде (как в AccountNavV5).
    const [appWindow] = useState(isAppWindow);
    // Ответ списка админов — запомнить (или забыть) этого человека.
    useEffect(() => {
        if (adminLoading || !email) return;
        rememberShellAdmin(email, isRealAdmin);
    }, [adminLoading, email, isRealAdmin]);
    // Пока список грузится — тот, кого браузер помнит админом (и не «смотрит как пользователь»).
    const remembered = adminLoading && !previewAsUser && !!email && getShellAdminHint() === email.toLowerCase();
    const allowed = APP_SHELL_FOR_ALL || navV5 || remembered;
    return {
        enabled: APP_SHELL_PREVIEW && allowed && phone && !optOut,
        loading: !APP_SHELL_FOR_ALL && adminLoading,
        appWindow,
        phone,
    };
};

/**
 * Показывает ли кабинет ряд разделов вкладки (TabSections): телефон и
 * оболочка «как приложение» или окно приложения — у обычного человека из
 * приложения тоже. Страница по нему убирает то, что ряд заменяет (ссылку
 * «Назад к чату» у «Базы знаний»).
 */
export const tabSectionsShown = (s: Pick<AppShellState, "enabled" | "appWindow" | "phone">): boolean =>
    TAB_SECTIONS_ON && s.phone && (s.enabled || s.appWindow);

export const useTabSectionsShown = (): boolean => tabSectionsShown(useAppShell());

/**
 * Пометки html[data-app-shell] и html[data-app-window], пока оболочка
 * включена; снимаются при выключении и уходе из кабинета. Их ставит
 * PhoneShell — один на кабинет.
 */
export const useShellRootAttributes = (enabled: boolean, appWindow: boolean): void => {
    useEffect(() => {
        if (!enabled) return;
        const root = document.documentElement;
        root.setAttribute(APP_SHELL_ATTR, "");
        if (appWindow) root.setAttribute(APP_WINDOW_ATTR, "");
        return () => {
            root.removeAttribute(APP_SHELL_ATTR);
            root.removeAttribute(APP_WINDOW_ATTR);
        };
    }, [enabled, appWindow]);
};
